/**
 * Thai national ID card validator (mod-11 checksum).
 * Reference: https://en.wikipedia.org/wiki/National_identification_number#Thailand
 *
 *   sum = Σ digit[i] * (13 - i) for i in 0..11
 *   check = (11 - (sum % 11)) % 10
 *   valid if check === digit[12]
 */
export function isValidThaiId(idcard: string): boolean {
  if (!/^\d{13}$/.test(idcard)) return false;
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(idcard.charAt(i), 10) * (13 - i);
  }
  const check = (11 - (sum % 11)) % 10;
  return check === parseInt(idcard.charAt(12), 10);
}

/** Format `1234567890123` → `1-2345-67890-12-3` for display. */
export function formatThaiId(idcard: string): string {
  if (!/^\d{13}$/.test(idcard)) return idcard;
  return `${idcard[0]}-${idcard.slice(1, 5)}-${idcard.slice(5, 10)}-${idcard.slice(10, 12)}-${idcard[12]}`;
}

/**
 * Progressive format while the user is typing. Renders a partial dashed view
 * for any digit count 0..13 so the input always reads as a real Thai ID.
 *   ""           → ""
 *   "1"          → "1"
 *   "12345"      → "1-2345"
 *   "123456789"  → "1-2345-6789"
 *   "1234567890123" → "1-2345-67890-12-3"
 */
export function formatThaiIdPartial(digits: string): string {
  const d = digits.replace(/\D/g, "").slice(0, 13);
  if (d.length === 0) return "";
  if (d.length === 1) return d;
  if (d.length <= 5) return `${d[0]}-${d.slice(1)}`;
  if (d.length <= 10) return `${d[0]}-${d.slice(1, 5)}-${d.slice(5)}`;
  if (d.length <= 12)
    return `${d[0]}-${d.slice(1, 5)}-${d.slice(5, 10)}-${d.slice(10)}`;
  return `${d[0]}-${d.slice(1, 5)}-${d.slice(5, 10)}-${d.slice(10, 12)}-${d[12]}`;
}
